import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Table from "../../../components/table";
import Button from "../../../components/button";
import Tabs, { TabItem } from "../../../components/tabs";
import { SearchBox } from "../../../components/input-functions";
import useScreenName from "../../../hooks/useScreenName";
import { dateToDDMMYYYY, splitKeyWithSpace } from "../../../utils";
import { RoutePaths } from "../../../models";
import dispatch from "../../../middleware";
import { createPricing, listPricings } from "../../../actions/credit-decision.action";
import {
  CreditDecisionPricing,
  CreditDecisionPricingListResponse,
  InterestRateTypeEnum,
  ProcessingFeeTypeEnum,
} from "../../../models/creditDecision";

type Tab = "list" | "create";

interface SlabRow {
  fromAmount: string;
  tillAmount: string;
  processingFeeWithGst: string;
  processingFeeType: ProcessingFeeTypeEnum;
  irpa: string;
  interestType: InterestRateTypeEnum;
}

const tabItems: TabItem<Tab>[] = [
  { label: "List", value: "list" },
  { label: "Create", value: "create" },
];

const emptySlab = (): SlabRow => ({
  fromAmount: "",
  tillAmount: "",
  processingFeeWithGst: "",
  processingFeeType: ProcessingFeeTypeEnum.PERCENTAGE,
  irpa: "",
  interestType: InterestRateTypeEnum.FLAT_BALANCE_RATE,
});

const listColumns = [
  { field: "id", label: "ID" },
  { field: "name", label: "Name" },
  { field: "slabCount", label: "Slabs" },
  { field: "createdAt", label: "Created At" },
];

export default function PricingPage() {
  useScreenName("Credit Decision Pricing");
  const history = useHistory();
  const storeDispatch = useDispatch();
  const [tab, setTab] = useState<Tab>("list");
  const [pricings, setPricings] = useState<CreditDecisionPricing[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [nameSearch, setNameSearch] = useState("");
  const initFetchedRef = useRef(false);
  const [name, setName] = useState("");
  const [slabs, setSlabs] = useState<SlabRow[]>([emptySlab()]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  async function fetchList(reset?: boolean) {
    try {
      setListLoading(true);
      const query: Record<string, any> = { limit: 20 };
      if (nameSearch) query.name = nameSearch;
      if (!reset && cursor) query.cursor = cursor;
      const response: CreditDecisionPricingListResponse = await dispatch(
        storeDispatch,
        listPricings(query)
      );
      setPricings((prev) => (reset ? response.data : [...prev, ...response.data]));
      setCursor(response.nextCursor);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error("Error fetching pricings:", error);
      alert("Failed to fetch pricings");
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    if (!initFetchedRef.current) {
      initFetchedRef.current = true;
      fetchList(true);
      return;
    }
    fetchList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameSearch]);

  function updateSlab(index: number, field: keyof SlabRow, value: string) {
    setSlabs((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }

  function addSlab() {
    setSlabs((prev) => [...prev, emptySlab()]);
  }

  function removeSlab(index: number) {
    setSlabs((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    setError("");
    setResult("");
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    const pricingSlabs = slabs.map((s) => ({
      fromAmount: Number(s.fromAmount),
      tillAmount: Number(s.tillAmount),
      processingFeeWithGst: Number(s.processingFeeWithGst),
      processingFeeType: s.processingFeeType,
      irpa: Number(s.irpa),
      interestType: s.interestType,
    }));

    try {
      setLoading(true);
      const response = await dispatch(
        storeDispatch,
        createPricing({ name: name.trim(), pricingSlabs })
      );
      setResult(`Pricing created: ${response._id}`);
      setName("");
      setSlabs([emptySlab()]);
      await fetchList(true);
    } catch (err: any) {
      setError(err.message || "Failed to create pricing");
    } finally {
      setLoading(false);
    }
  }

  function handleView(pricingId: string) {
    history.push(RoutePaths.CREDIT_DECISION_PRICING_DETAIL.replace(":id", pricingId));
  }

  return (
    <div>
      <h2 className="mb-4">Credit Decision Pricing</h2>
      <div className="dashboard-container">
        <div className="mb-4">
          <Tabs items={tabItems} value={tab} onChange={setTab} />
        </div>

        {tab === "list" && (
          <div>
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6 col-lg-4">
                <label className="form-label">Search by Name</label>
                <SearchBox placeholder="Search by name" value={nameSearch} onSubmit={setNameSearch} />
              </div>
            </div>
            <Table
              loading={listLoading}
              columns={listColumns}
              data={pricings.map((p) => ({
                id: <Button variant="blue-text" size="xs" onClick={() => handleView(p._id)}>{p._id}</Button>,
                name: p.name,
                slabCount: p.pricingSlabs.length,
                createdAt: p.createdAt ? dateToDDMMYYYY(p.createdAt) : "-",
              }))}
            />
            {hasMore && (
              <div className="text-center mt-3">
                <Button variant="light-blue" onClick={() => fetchList(false)} disabled={listLoading}>
                  {listLoading ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        )}

        {tab === "create" && (
          <div>
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-6 col-lg-4">
                <label className="form-label">Pricing Name *</label>
                <input type="text" className="form-control" value={name} placeholder="e.g. Standard EWA Pricing" onChange={(e) => setName(e.target.value)} />
              </div>
            </div>
            <h6 className="mb-3">Pricing Slabs</h6>
            {slabs.map((slab, index) => (
              <div key={index} className="card mb-3 p-3 bg-light border">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong>Slab #{index + 1}</strong>
                  {slabs.length > 1 && <Button variant="danger-fill" size="xs" onClick={() => removeSlab(index)}>Remove</Button>}
                </div>
                <div className="row g-3">
                  <div className="col-12 col-md-6 col-lg-4"><label className="form-label">From Amount *</label><input type="number" className="form-control" value={slab.fromAmount} onChange={(e) => updateSlab(index, "fromAmount", e.target.value)} /></div>
                  <div className="col-12 col-md-6 col-lg-4"><label className="form-label">Till Amount *</label><input type="number" className="form-control" value={slab.tillAmount} onChange={(e) => updateSlab(index, "tillAmount", e.target.value)} /></div>
                  <div className="col-12 col-md-6 col-lg-4"><label className="form-label">Processing Fee With GST *</label><input type="number" className="form-control" value={slab.processingFeeWithGst} onChange={(e) => updateSlab(index, "processingFeeWithGst", e.target.value)} /></div>
                  <div className="col-12 col-md-6 col-lg-4"><label className="form-label">Processing Fee Type *</label><select className="form-control" value={slab.processingFeeType} onChange={(e) => updateSlab(index, "processingFeeType", e.target.value)}>{Object.values(ProcessingFeeTypeEnum).map((v) => <option key={v} value={v}>{splitKeyWithSpace(v)}</option>)}</select></div>
                  <div className="col-12 col-md-6 col-lg-4"><label className="form-label">IRPA *</label><input type="number" className="form-control" value={slab.irpa} onChange={(e) => updateSlab(index, "irpa", e.target.value)} /></div>
                  <div className="col-12 col-md-6 col-lg-4"><label className="form-label">Interest Type *</label><select className="form-control" value={slab.interestType} onChange={(e) => updateSlab(index, "interestType", e.target.value)}>{Object.values(InterestRateTypeEnum).map((v) => <option key={v} value={v}>{splitKeyWithSpace(v)}</option>)}</select></div>
                </div>
              </div>
            ))}
            <div className="d-flex gap-2 mt-3">
              <Button variant="blue-outline" onClick={addSlab}>Add Slab</Button>
              <Button variant="primary" onClick={handleSubmit} disabled={loading}>{loading ? "Creating..." : "Create Pricing"}</Button>
            </div>
            {error && <div className="alert alert-danger mt-3">{error}</div>}
            {result && <div className="alert alert-info mt-3">{result}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
